import { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { createChantier } from '../lib/chantierService';
import { uploadChantierPhoto } from '../lib/fileService';
import './NewChantierModal.css';

function NewChantierModal({ onClose, onCreated }) {
  const { session } = useAuth();
  const userId = session?.user?.id;

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const photoInputRef = useRef(null);

  const canCreate = name.trim().length > 0 && !creating;

  function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  async function uploadPhotoIfAny() {
    if (!photoFile) return undefined;
    try {
      return await uploadChantierPhoto(photoFile);
    } catch (err) {
      // Non bloquant, comme sur mobile — mais on log pour pouvoir diagnostiquer.
      console.error('Upload photo error:', err);
      return undefined;
    }
  }

  async function handleCreate() {
    if (!canCreate) return;
    setCreating(true);
    setError(null);

    try {
      const photoUrl = await uploadPhotoIfAny();
      const chantier = await createChantier(userId, {
        name: name.trim(),
        address: address.trim() || undefined,
        postal_code: postalCode.trim() || undefined,
        city: city.trim() || undefined,
        photo_url: photoUrl,
        phase: 'prospect',
      });
      onCreated(chantier);
    } catch (err) {
      setError(`Impossible de créer le projet : ${err.message}`);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="ModalOverlay" onClick={creating ? undefined : onClose}>
      <div className="Modal" onClick={(e) => e.stopPropagation()}>
        <div className="Modal-header">
          <div>
            <h2 className="Modal-title">Nouveau projet</h2>
            <p className="Modal-subtitle">Informations de base</p>
          </div>
          <button
            className="Modal-close"
            onClick={onClose}
            disabled={creating}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        <div className="Modal-body">
          {error && <p className="Modal-error">{error}</p>}

          <div className="Modal-form">
            <div
              className="PhotoPicker"
              onClick={() => photoInputRef.current?.click()}
            >
              {photoPreview ? (
                <img src={photoPreview} alt="Projet" className="PhotoPicker-image" />
              ) : (
                <div className="PhotoPicker-placeholder">
                  <span>Ajouter une photo</span>
                  <span className="PhotoPicker-sub">Optionnel</span>
                </div>
              )}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handlePhotoChange}
              />
            </div>

            <label className="Field">
              <span className="Field-label">
                Nom du projet <span className="Field-required">*</span>
              </span>
              <input
                className="Field-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex : Rénovation villa Dupont"
                autoFocus
              />
            </label>

            <label className="Field">
              <span className="Field-label">Numéro et rue</span>
              <input
                className="Field-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Ex : 12 rue des Lilas"
              />
            </label>

            <div className="Field-row">
              <label className="Field Field-postalCode">
                <span className="Field-label">Code postal</span>
                <input
                  className="Field-input"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="75011"
                  inputMode="numeric"
                  maxLength={5}
                />
              </label>

              <label className="Field Field-city">
                <span className="Field-label">Ville</span>
                <input
                  className="Field-input"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Paris"
                />
              </label>
            </div>

            <p className="Modal-info">
              Le projet démarre vierge. Vous le compléterez au fil de ses phases.
            </p>
          </div>
        </div>

        <div className="Modal-footer">
          <button className="Modal-btn Modal-btn-primary" onClick={handleCreate} disabled={!canCreate}>
            {creating ? (
              <span className="Modal-btn-loading">
                <span className="Modal-btnSpinner" />
                Création en cours…
              </span>
            ) : (
              'Créer le projet'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewChantierModal;
